import type { WalletClient } from 'viem'

import { RADIX_DECIMAL } from '@cowprotocol/common-const'
import {
  COW_PROTOCOL_SETTLEMENT_CONTRACT_ADDRESS,
  formatSymbol,
  formatTokenAmount,
  getCurrencyAddress,
  isAddress,
  isBarnBackendEnv,
  isSellOrder,
  shortenAddress,
} from '@cowprotocol/common-utils'
import {
  OrderClass,
  OrderKind,
  OrderSigningUtils,
  SigningScheme,
  SupportedChainId as ChainId,
  UnsignedOrder,
  type Signer,
} from '@cowprotocol/cow-sdk'
import { Currency, CurrencyAmount, Token } from '@cowprotocol/currency'

import { t } from '@lingui/core/macro'
import { orderBookApi } from 'cowSdk'

import { ChangeOrderStatusParams, Order, OrderStatus } from 'legacy/state/orders/actions'

import { AppDataInfo } from 'modules/appData'

import { getIsOrderBookTypedError } from 'api/cowProtocol'
import { ApiErrorObject, OperatorError } from 'api/cowProtocol/errors/OperatorError'

export type MapUnsignedOrderToOrderParams = {
  unsignedOrder: UnsignedOrder
  additionalParams: UnsignedOrderAdditionalParams
}

export type PostOrderParams = {
  account: string
  chainId: ChainId
  signer: WalletClient
  kind: OrderKind
  inputAmount: CurrencyAmount<Currency>
  outputAmount: CurrencyAmount<Currency>
  bridgeOutputAmount?: CurrencyAmount<Currency>
  sellAmountBeforeFee: CurrencyAmount<Currency>
  feeAmount: CurrencyAmount<Currency> | undefined
  sellToken: Token
  buyToken: Token
  validTo: number
  recipient: string
  recipientAddressOrName?: string | null
  allowsOffchainSigning: boolean
  appData: AppDataInfo
  class: OrderClass
  partiallyFillable: boolean
  quoteId?: number
  isSafeWallet: boolean
}

export type SignOrderParams = {
  quoteId: number | undefined
  order: UnsignedOrder
}

export type UnsignedOrderAdditionalParams = Omit<PostOrderParams, 'signer' | 'validTo'> & {
  orderId: string
  signature: string
  signingScheme: SigningScheme
  isOnChain?: boolean
  orderCreationHash?: string
}

type OrderCancellationParams = {
  orderId: string
  account: string
  chainId: ChainId
  signer: Signer
  cancelPendingOrder: (params: ChangeOrderStatusParams) => void
}

export function getOrderSubmitSummary(
  params: Pick<
    PostOrderParams,
    'kind' | 'account' | 'inputAmount' | 'outputAmount' | 'recipient' | 'recipientAddressOrName' | 'feeAmount'
  >,
): string {
  const { kind, account, inputAmount, outputAmount, recipient, recipientAddressOrName, feeAmount } = params

  const sellToken = inputAmount.currency
  const buyToken = outputAmount.currency

  const inputSymbol = formatSymbol(sellToken.symbol) ?? ''
  const outputSymbol = formatSymbol(buyToken.symbol) ?? ''
  // this already contains the fee in the fee amount when fee=0
  const inputAmountValue = formatTokenAmount(feeAmount ? inputAmount.add(feeAmount) : inputAmount)
  const outputAmountValue = formatTokenAmount(outputAmount)

  const isSell = isSellOrder(kind)

  if (recipient === account) {
    return isSell
      ? t`Swap ${inputAmountValue} ${inputSymbol} for at least ${outputAmountValue} ${outputSymbol}`
      : t`Swap at most ${inputAmountValue} ${inputSymbol} for ${outputAmountValue} ${outputSymbol}`
  }

  const toAddress =
    recipientAddressOrName && isAddress(recipientAddressOrName)
      ? shortenAddress(recipientAddressOrName)
      : (recipientAddressOrName ?? '')

  // Spelled out per variant rather than wrapping the localized summary in an outer message:
  // interpolating an already-translated sentence leaves translators unable to reorder it.
  return isSell
    ? t`Swap ${inputAmountValue} ${inputSymbol} for at least ${outputAmountValue} ${outputSymbol} to ${toAddress}`
    : t`Swap at most ${inputAmountValue} ${inputSymbol} for ${outputAmountValue} ${outputSymbol} to ${toAddress}`
}

export function getSignOrderParams(params: PostOrderParams): SignOrderParams {
  const {
    kind,
    inputAmount,
    sellAmountBeforeFee,
    outputAmount,
    sellToken,
    buyToken,
    validTo,
    recipient,
    partiallyFillable,
    appData,
    quoteId,
  } = params
  const sellTokenAddress = sellToken.address
  const stringifiedJSON = JSON.stringify(sellToken, undefined, 2)

  if (!sellTokenAddress) {
    throw new Error(t`Order params invalid sellToken address for token: ${stringifiedJSON}`)
  }

  const isSellTrade = isSellOrder(kind)

  // fee adjusted input amount
  const sellAmount = (isSellTrade ? sellAmountBeforeFee : inputAmount).quotient.toString(RADIX_DECIMAL)
  // slippage adjusted output amount
  const buyAmount = outputAmount.quotient.toString(RADIX_DECIMAL)

  const receiver = recipient

  return {
    quoteId,
    order: {
      sellToken: sellTokenAddress,
      buyToken: getCurrencyAddress(buyToken),
      sellAmount,
      buyAmount,
      validTo,
      appData: appData.appDataKeccak256,
      feeAmount: '0',
      kind,
      receiver,
      partiallyFillable,
    },
  }
}

export function mapUnsignedOrderToOrder({ unsignedOrder, additionalParams }: MapUnsignedOrderToOrderParams): Order {
  const {
    orderId,
    account,
    sellToken,
    buyToken,
    allowsOffchainSigning,
    isOnChain,
    signature,
    signingScheme,
    sellAmountBeforeFee,
    orderCreationHash,
    quoteId,
    appData: { fullAppData },
    bridgeOutputAmount,
  } = additionalParams
  const status = _getOrderStatus(allowsOffchainSigning, isOnChain)

  return {
    ...unsignedOrder,

    // Basic order params
    id: orderId,
    owner: account,
    inputToken: sellToken,
    outputToken: buyToken,
    quoteId,
    class: additionalParams.class,
    fullAppData,
    bridgeOutputAmount,

    // Status
    status,
    creationTime: new Date().toISOString(),

    // EthFlow
    orderCreationHash,

    // Signature
    signature,
    signingScheme,

    // Additional API info
    apiAdditionalInfo: undefined,

    // sell amount BEFORE fee - necessary for later calculations (unfilled orders)
    sellAmountBeforeFee: sellAmountBeforeFee.quotient.toString(),
  }
}

export async function sendOrderCancellation(params: OrderCancellationParams): Promise<void> {
  const { orderId, chainId, signer, cancelPendingOrder } = params

  const { signature, signingScheme } = await OrderSigningUtils.signOrderCancellation(orderId, chainId, signer, {
    env: isBarnBackendEnv ? 'staging' : 'prod',
    settlementContractOverride: COW_PROTOCOL_SETTLEMENT_CONTRACT_ADDRESS,
  })

  if (!signature) throw new Error(t`Signature is undefined!`)

  await wrapErrorInOperatorError(async () => {
    await orderBookApi.sendSignedOrderCancellations(
      {
        orderUids: [orderId],
        signature,
        signingScheme,
      },
      { chainId },
    )

    cancelPendingOrder({ chainId, id: orderId })
  })
}

export async function wrapErrorInOperatorError<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn()
  } catch (e) {
    // In case it's an orderbook error, wrap it in an OperatorError
    if (getIsOrderBookTypedError(e)) {
      throw new OperatorError(e.body as ApiErrorObject)
    }
    throw e
  }
}

function _getOrderStatus(allowsOffchainSigning: boolean, isOnChain: boolean | undefined): OrderStatus {
  if (isOnChain) {
    return OrderStatus.CREATING
  } else if (!allowsOffchainSigning) {
    return OrderStatus.PRESIGNATURE_PENDING
  } else {
    return OrderStatus.PENDING
  }
}
